// components/recipes/workflow/mapping.ts
//
// Pure store<->view mapping for the Workflow panel, factored out of
// WorkflowPanel so the parity harness can exercise it against real prod
// recipe shapes without rendering React. Read side: buildWorkflowModel.
// Write side: makeWorkflowHandlers (mirrors legacy setAction/setFrequency).
import { toJS } from 'mobx'
import {
    buildSummary,
    cadenceAllowsSchedule,
    executionModeForRecipe,
    isNumericAction,
    isStateAction,
    readSchedule,
    type NumericOp,
    type Repeat,
    type Restriction,
    type WorkflowCapabilities,
} from './model'
import type { WorkflowViewModel, WorkflowViewHandlers } from './WorkflowSentenceView'
import type { Day } from './DayOfWeekToggle'
import {
    ActionsList,
    FrequenciesList,
    NumericalChangeActions,
} from '../../../util/contstants'
import { State } from '../../../constants'
import {
    defaultGuidedScheduleDraft,
    type ScheduleEditorProps,
} from '../../../features/scheduled-segments/components/ScheduleEditor'

export interface StoreLike {
    recipe: any
    variables?: Record<string, { type: string; name: string; formula: string }>
    version?: number
    availableActions: { value: number; label: string }[]
    setAction(action: number, params: any): void
    setFrequency(frequency: number, params: any): void
    setEnabled(enabled: boolean): void
    scheduleDraft?: ScheduleEditorProps['draft']
    scheduleTimezones?: ScheduleEditorProps['timezones']
    setScheduleDraft?(draft: ScheduleEditorProps['draft']): void
}

export const numericVariableOptions = (vars: StoreLike['variables']) =>
    Object.values(vars ?? {})
        .filter((v) => v.type === 'number')
        .map((v) => ({ name: v.name }))

// ── Read: recipe -> view model ──────────────────────────────────────────────
export const buildWorkflowModel = (
    recipe: any,
    opts: {
        actionOptions: { value: number; label: string }[]
        variableOptions: { name: string }[]
        version: number
        currency: string
        clockSchedulingAvailable: boolean
        scheduleEditor?: ScheduleEditorProps
    },
): WorkflowViewModel => {
    const params: any = recipe.action_params ?? {}
    const extra: any = params.extra
    const numeric = isNumericAction(recipe.action) && extra && typeof extra === 'object'
    const fp: any = recipe.frequency_params ?? {}

    const op = (numeric ? extra.action : NumericalChangeActions.DecreasePercent) as NumericOp
    const source: 'value' | 'variable' =
        numeric && extra.source === 'variable' ? 'variable' : 'value'
    const rawValue = numeric && typeof extra.value === 'number' ? extra.value : 0
    const variableName = numeric && typeof extra.value === 'string' ? extra.value : ''
    const stateValue =
        isStateAction(recipe.action) && typeof extra === 'number' ? extra : State.Paused
    const strategy =
        recipe.action === ActionsList.ChangeBiddingStrategy &&
        (extra === 'SALES_DOWN_ONLY' || extra === 'SALES' || extra === 'NONE')
            ? extra
            : 'SALES_DOWN_ONLY'
    const placements =
        recipe.action === ActionsList.ChangeBidAdjustment && extra && typeof extra === 'object'
            ? extra
            : undefined
    const { cadence, restriction } = readSchedule(recipe.frequency, fp)
    const days = (fp.days ?? []) as Day[]
    const date = fp.date instanceof Date ? fp.date : new Date()
    const repeat = (fp.repeat ?? 'monthly') as Repeat

    return {
        enabled: recipe.enabled,
        actionOptions: opts.actionOptions,
        action: recipe.action,
        op,
        source,
        rawValue,
        variableName,
        variableOptions: opts.variableOptions,
        variableAllowed: opts.version === 2,
        currency: opts.currency,
        state: stateValue,
        strategy,
        placements,
        cadence,
        restriction,
        days,
        date,
        repeat,
        executionMode: executionModeForRecipe(
            recipe.frequency,
            Boolean(opts.scheduleEditor?.draft),
            { clockSchedulingAvailable: opts.clockSchedulingAvailable },
        ),
        clockSchedulingAvailable: opts.clockSchedulingAvailable,
        scheduleEditor: opts.scheduleEditor,
        summary: buildSummary({
            action: recipe.action,
            op,
            source,
            rawValue,
            variableName,
            state: stateValue,
            currency: opts.currency,
            cadence,
            restriction,
            days,
            date,
            repeat,
        }),
    }
}

// ── Write: handlers that mutate the store (legacy-equivalent payloads) ───────
export const makeWorkflowHandlers = (
    store: StoreLike,
    { clockSchedulingAvailable }: WorkflowCapabilities = { clockSchedulingAvailable: true },
): WorkflowViewHandlers => {
    // toJS (not cloneDeep) so we copy plain values without dragging the MobX
    // observability symbol into the saved payload.
    const currentExtra = () => {
        const extra = store.recipe.action_params?.extra
        return typeof extra === 'object' && extra ? toJS(extra) : {}
    }

    // Numeric edits inject source:'value' when absent — matches the legacy
    // ActionNumericalChange behaviour (a touched legacy row gains source).
    const setNumeric = (patch: Record<string, any>) => {
        const next = { ...currentExtra(), ...patch }
        if (!next.source) next.source = 'value'
        store.setAction(store.recipe.action, { type: store.recipe.action, extra: next })
    }

    const fp = () => store.recipe.frequency_params ?? {}

    return {
        onToggleEnabled: () => store.setEnabled(!store.recipe.enabled),
        onExecutionMode: (mode) => {
            // Explicit mode selection is the compatibility boundary. Do not
            // normalise legacy 2/4/6 values merely by opening this panel.
            if (mode === 'after-sync') {
                store.setFrequency(FrequenciesList.AfterEveryDataSync, undefined)
                store.setScheduleDraft?.(null)
            } else if (mode === 'scheduled') {
                if (!clockSchedulingAvailable) return
                store.setFrequency(FrequenciesList.Scheduled, undefined)
                if (!store.scheduleDraft) {
                    store.setScheduleDraft?.(
                        defaultGuidedScheduleDraft(store.scheduleTimezones?.default ?? 'UTC'),
                    )
                }
            } else {
                store.setFrequency(FrequenciesList.Manually, undefined)
                store.setScheduleDraft?.(null)
            }
        },
        onAction: (value: number) => store.setAction(value, { type: value }),
        onOp: (op: NumericOp) => setNumeric({ action: op }),
        onValue: (rawValue: number) => setNumeric({ value: rawValue }),
        onSource: (next: 'value' | 'variable') => {
            // Only reset the value when the source actually changes (matches the
            // legacy guard `v.source !== params.source`). Re-selecting the active
            // source must not wipe the user's amount / variable.
            const cur = currentExtra()
            const currentSource = cur.source === 'variable' ? 'variable' : 'value'
            if (next === currentSource) return
            const opts = numericVariableOptions(store.variables)
            const value = next === 'value' ? 0 : opts.length > 0 ? opts[0].name : ''
            store.setAction(store.recipe.action, {
                type: store.recipe.action,
                extra: { ...cur, source: next, value },
            })
        },
        onVariable: (name: string) => setNumeric({ source: 'variable', value: name }),
        onState: (state: number) =>
            store.setAction(store.recipe.action, { type: store.recipe.action, extra: state }),
        onStrategy: (strategy) =>
            store.setAction(ActionsList.ChangeBiddingStrategy, {
                type: ActionsList.ChangeBiddingStrategy,
                extra: strategy,
            }),
        // A null value drops the key. The recipe only touches the placements
        // present in `extra`, so an absent key means "leave it unchanged".
        onPlacement: (placement, value) => {
            const next = { ...currentExtra() }
            if (value === null) {
                delete next[placement]
            } else {
                next[placement] = value
            }
            store.setAction(ActionsList.ChangeBidAdjustment, {
                type: ActionsList.ChangeBidAdjustment,
                extra: next,
            })
        },

        // Cadence = how often. Keep the current restriction (re-stamping its
        // sub-frequency); Manually/At-a-certain-time can't be restricted.
        onCadence: (cadence: number) => {
            const { restriction } = readSchedule(store.recipe.frequency, fp())
            if (restriction === 'days' && cadenceAllowsSchedule(cadence)) {
                store.setFrequency(FrequenciesList.OnCertainDays, { ...fp(), frequency: cadence })
            } else if (restriction === 'dates' && cadenceAllowsSchedule(cadence)) {
                store.setFrequency(FrequenciesList.OnCertainDates, { ...fp(), frequency: cadence })
            } else {
                store.setFrequency(cadence, undefined)
            }
        },

        // Restriction = which days. Stamps the current cadence into the nested
        // sub-frequency; seeds days/date/repeat from existing or legacy defaults.
        onRestriction: (restriction: Restriction) => {
            const { cadence } = readSchedule(store.recipe.frequency, fp())
            const cur = fp()
            if (restriction === 'days') {
                store.setFrequency(FrequenciesList.OnCertainDays, {
                    frequency: cadence,
                    days: cur.days ?? ['sunday'],
                })
            } else if (restriction === 'dates') {
                store.setFrequency(FrequenciesList.OnCertainDates, {
                    frequency: cadence,
                    date: cur.date instanceof Date ? cur.date : new Date(),
                    repeat: cur.repeat ?? 'monthly',
                })
            } else {
                store.setFrequency(cadence, undefined)
            }
        },

        onDays: (days: Day[]) =>
            store.setFrequency(FrequenciesList.OnCertainDays, { ...fp(), days }),
        onDate: (date: Date) =>
            store.setFrequency(FrequenciesList.OnCertainDates, { ...fp(), date }),
        onRepeat: (repeat: Repeat) =>
            store.setFrequency(FrequenciesList.OnCertainDates, { ...fp(), repeat }),
    }
}
